import { searchCustomers } from '../api/customerApi';
import { challanApi } from '../api/challanApi';
import { getCylinders } from '../api/cylinderMasterApi';
import { getAllGoods } from '../api/goodsApi';
import { userApi } from '../api/userApi';
import vendorApi from '../api/vendorApi';

export const SEARCH_CONFIG = {
  Customers: {
    search: async (query, page = 0, size = 10) => {
      const trimmed = query.trim();
      if (!trimmed) return [];

      const params = trimmed.includes('@') ? { email: trimmed } : { name: trimmed };
      const response = await searchCustomers({ ...params, page, size });
      const records = response?.data?.content || response?.data || [];
      const customers = Array.isArray(records) ? records : [];
      
      return {
        content: customers,
        totalPages: response?.data?.totalPages || 0,
        totalElements: response?.data?.totalElements || customers.length,
      };
    },
    normalize: (customer) => ({
      id: customer?.id,
      title: customer?.name || customer?.customerName || 'Customer',
      subtitle: customer?.mobile || customer?.contactNumber || customer?.phone || 'No contact',
      meta: customer?.email || customer?.customerEmail || '',
      type: 'Customer',
      details: [
        { label: 'ID', value: customer?.customerCode || customer?.customerId || customer?.id },
        { label: 'Balance', value: customer?.deposite ?? customer?.balance },
      ],
      route: '/dashboard/customers',
      original: customer,
    }),
    navigate: (result, navigate) => {
      const quickSearch = result?.title || result?.subtitle || result?.meta || result?.original?.name || result?.original?.email || '';
      navigate('/dashboard/customers', { state: quickSearch ? { quickSearch } : undefined });
    },
  },
  'Items & Goods': {
    search: async (query, page = 0, size = 10) => {
      const trimmed = query.trim();
      if (!trimmed) return [];

      const response = await getAllGoods();
      const records = response?.data?.content || response?.data || [];
      const goods = Array.isArray(records) ? records : [];
      const normalizedQuery = trimmed.toLowerCase();

      const filtered = goods
        .filter((good) => {
          const searchableFields = [
            good?.name,
            good?.productCode,
            good?.description,
            good?.category,
            good?.brand,
            good?.company,
          ];

          return searchableFields.some((value) =>
            String(value || '').toLowerCase().includes(normalizedQuery)
          );
        });

      const start = page * size;
      const end = start + size;
      const paginatedResults = filtered.slice(start, end);

      return {
        content: paginatedResults,
        totalPages: Math.ceil(filtered.length / size),
        totalElements: filtered.length,
      };
    },
    normalize: (good) => ({
      id: good?.id,
      title: good?.name || good?.productCode || 'Goods',
      subtitle: `Stock: ${good?.stock ?? 'N/A'}`,
      meta: good?.price ?? good?.salePrice ?? '',
      type: 'Goods',
      details: [
        { label: 'Code', value: good?.productCode },
        { label: 'Category', value: good?.category },
        { label: 'Stock', value: good?.stock },
      ],
      route: good?.id ? `/dashboard/inventory/goods/${good.id}` : '/dashboard/inventory/goods',
      original: good,
    }),
    navigate: (result, navigate) => {
      if (result?.route) {
        const quickSearch = result?.title || result?.subtitle || result?.meta || result?.original?.name || result?.original?.productCode || '';
        navigate('/dashboard/inventory/goods', { state: quickSearch ? { quickSearch } : undefined });
      }
    },
  },
  'Customer Passbook': {
    search: async (query) => {
      const trimmed = query.trim();
      if (!trimmed) return [];
      return SEARCH_CONFIG.Customers.search(query);
    },
    normalize: (customer) => ({
      id: customer?.id,
      title: customer?.name || customer?.customerName || 'Customer',
      subtitle: customer?.mobile || customer?.contactNumber || customer?.phone || 'Open passbook',
      meta: customer?.email || customer?.customerEmail || '',
      type: 'Customer Passbook',
      details: [{ label: 'Customer ID', value: customer?.id }],
      route: '/dashboard/customers/passbook',
      original: customer,
    }),
    navigate: (result, navigate) => {
      const customerId = result?.id || result?.original?.id;
      if (!customerId) return;
      navigate('/dashboard/customers/passbook', {
        state: {
          customerId,
          customerName: result?.title || result?.original?.name || 'Customer',
          customerEmail: result?.meta || result?.original?.email || '',
        },
      });
    },
  },
  'Billing Statements': {
    search: async (query) => {
      const trimmed = query.trim();
      if (!trimmed) return [];
      return SEARCH_CONFIG.Customers.search(query);
    },
    normalize: (customer) => ({
      id: customer?.id,
      title: customer?.name || customer?.customerName || 'Customer',
      subtitle: customer?.mobile || customer?.contactNumber || customer?.phone || 'Statement',
      meta: customer?.email || customer?.customerEmail || '',
      type: 'Billing',
      details: [{ label: 'Customer ID', value: customer?.id }],
      route: '/dashboard/billing-statements',
      original: customer,
    }),
    navigate: (result, navigate) => {
      const customerId = result?.id || result?.original?.id;
      if (!customerId) return;
      navigate('/dashboard/billing-statements', {
        state: {
          selectedCustomerId: customerId,
          customerName: result?.title || result?.original?.name || 'Customer',
        },
      });
    },
  },
  Challans: {
    search: async (query, page = 0, size = 10) => {
      const trimmed = query.trim();
      if (!trimmed) return [];

      const attempts = [
        { challanNumber: trimmed },
        { customerName: trimmed },
        { customerId: trimmed },
        { status: trimmed },
        { movementType: trimmed },
      ];

      for (const params of attempts) {
        try {
          const response = await challanApi.searchChallans({ page, size, ...params });
          const payload = response?.data || {};
          const records = payload?.content || payload || [];
          if (Array.isArray(records) && records.length > 0) {
            return {
              content: records,
              totalPages: payload?.totalPages || 0,
              totalElements: payload?.totalElements || 0,
            };
          }
        } catch (error) {
          // Ignore invalid filter params and continue to the next supported variant.
        }
      }

      try {
        const response = await challanApi.getAllChallans();
        const records = response?.data?.content || response?.data || [];
        const challans = Array.isArray(records) ? records : [];
        const normalizedQuery = trimmed.toLowerCase();

        const filtered = challans
          .filter((challan) => {
            const searchableFields = [
              challan?.challanNumber,
              challan?.customerName,
              challan?.customerId,
              challan?.status,
              challan?.movementType,
            ];

            return searchableFields.some((value) =>
              String(value || '').toLowerCase().includes(normalizedQuery)
            );
          });

        const start = page * size;
        const end = start + size;
        const paginatedResults = filtered.slice(start, end);

        return {
          content: paginatedResults,
          totalPages: Math.ceil(filtered.length / size),
          totalElements: filtered.length,
        };
      } catch (error) {
        return { content: [], totalPages: 0, totalElements: 0 };
      }
    },
    normalize: (challan) => ({
      id: challan?.id,
      title: challan?.challanNumber || challan?.id || 'Challan',
      subtitle: challan?.customerName || challan?.customerId || '',
      meta: challan?.status || challan?.movementType || '',
      type: 'Challan',
      details: [
        { label: 'Movement', value: challan?.movementType },
        { label: 'Date', value: challan?.date || challan?.challanDate || challan?.createdAt },
      ],
      route: '/dashboard/challans/view',
      original: challan,
    }),
    navigate: (result, navigate) => {
      const quickSearch = result?.title || result?.subtitle || result?.meta || result?.original?.challanNumber || result?.original?.customerName || '';
      navigate('/dashboard/challans/view', { state: { selectedChallanId: result?.id, quickSearch: quickSearch || undefined } });
    },
  },
  Cylinders: {
    search: async (query, page = 0, size = 10) => {
      const trimmed = query.trim();
      if (!trimmed) return [];

      const attempts = [
        { cylinderName: trimmed },
        { srno: trimmed },
        { cylinderCapacity: trimmed },
        { testingDate: trimmed },
      ];

      for (const params of attempts) {
        try {
          const response = await getCylinders({ page, size, ...params });
          const payload = response?.data || {};
          const records = payload?.content || payload || [];
          if (Array.isArray(records) && records.length > 0) {
            return {
              content: records,
              totalPages: payload?.totalPages || 0,
              totalElements: payload?.totalElements || records.length,
            };
          }
        } catch (error) {
          // Ignore invalid filter param combos and continue with the next supported field.
        }
      }

      try {
        const response = await getCylinders({ page: 0, size: 100 });
        const payload = response?.data || {};
        const records = payload?.content || payload || [];
        const cylinders = Array.isArray(records) ? records : [];
        const normalizedQuery = trimmed.toLowerCase();

        const filtered = cylinders
          .filter((cylinder) => {
            const searchableFields = [
              cylinder?.cylinderName,
              cylinder?.srno,
              cylinder?.cylinderCapacity,
              cylinder?.testingDate,
              cylinder?.status,
            ];

            return searchableFields.some((value) =>
              String(value || '').toLowerCase().includes(normalizedQuery)
            );
          });

        const start = page * size;
        const end = start + size;
        const paginatedResults = filtered.slice(start, end);

        return {
          content: paginatedResults,
          totalPages: Math.ceil(filtered.length / size),
          totalElements: filtered.length,
        };
      } catch (error) {
        return { content: [], totalPages: 0, totalElements: 0 };
      }
    },
    normalize: (cylinder) => ({
      id: cylinder?.id,
      title: cylinder?.cylinderName || cylinder?.name || 'Cylinder',
      subtitle: cylinder?.srno || cylinder?.serialNumber || '',
      meta: cylinder?.status || cylinder?.condition || '',
      type: 'Cylinder',
      details: [
        { label: 'Capacity', value: cylinder?.cylinderCapacity },
        { label: 'Testing', value: cylinder?.testingDate },
      ],
      route: '/dashboard/cylinders/history',
      original: cylinder,
    }),
    navigate: (result, navigate) => {
      const quickSearch = result?.title || result?.subtitle || result?.meta || result?.original?.cylinderName || result?.original?.srno || '';
      navigate('/dashboard/inventory/cylinders', { state: { selectedCylinderId: result?.id, quickSearch: quickSearch || undefined } });
    },
  },
  Vendors: {
    search: async (query, page = 0, size = 10) => {
      const trimmed = query.trim();
      if (!trimmed) return [];

      const response = await vendorApi.getAll();
      const records = Array.isArray(response) ? response : response?.data || response?.vendors || [];
      const vendors = Array.isArray(records) ? records : [];
      const normalizedQuery = trimmed.toLowerCase();

      const filtered = vendors
        .filter((vendor) => {
          const searchableFields = [
            vendor?.companyName,
            vendor?.displayName,
            vendor?.name,
            vendor?.email,
            vendor?.phone,
            vendor?.mobile,
            vendor?.gstin,
          ];

          return searchableFields.some((value) =>
            String(value || '').toLowerCase().includes(normalizedQuery)
          );
        });

      const start = page * size;
      const end = start + size;
      const paginatedResults = filtered.slice(start, end);

      return {
        content: paginatedResults,
        totalPages: Math.ceil(filtered.length / size),
        totalElements: filtered.length,
      };
    },
    normalize: (vendor) => ({
      id: vendor?.id,
      title: vendor?.companyName || vendor?.displayName || vendor?.name || 'Vendor',
      subtitle: vendor?.phone || vendor?.mobile || 'No contact',
      meta: vendor?.email || vendor?.gstin || '',
      type: 'Vendor',
      details: [
        { label: 'GSTIN', value: vendor?.gstin },
        { label: 'ID', value: vendor?.vendorCode || vendor?.vendorId || vendor?.id },
      ],
      route: '/dashboard/vendors/view',
      original: vendor,
    }),
    navigate: (result, navigate) => {
      const quickSearch = result?.title || result?.subtitle || result?.meta || result?.original?.companyName || result?.original?.name || '';
      navigate('/dashboard/vendors/view', { state: { quickSearch: quickSearch || undefined } });
    },
  },
  'Vendor Passbook': {
    search: async (query) => {
      const trimmed = query.trim();
      if (!trimmed) return [];
      return SEARCH_CONFIG.Vendors.search(query);
    },
    normalize: (vendor) => ({
      id: vendor?.id,
      title: vendor?.companyName || vendor?.displayName || vendor?.name || 'Vendor',
      subtitle: vendor?.phone || vendor?.mobile || 'Open passbook',
      meta: vendor?.email || vendor?.gstin || '',
      type: 'Vendor Passbook',
      details: [{ label: 'Vendor ID', value: vendor?.id }],
      route: '/dashboard/purchases/vendor-passbook',
      original: vendor,
    }),
    navigate: (result, navigate) => {
      const vendorId = result?.id || result?.original?.id;
      if (!vendorId) return;
      navigate('/dashboard/purchases/vendor-passbook', {
        state: {
          vendorId,
          vendorName: result?.title || result?.original?.companyName || result?.original?.name || 'Vendor',
          vendorEmail: result?.meta || result?.original?.email || '',
        },
      });
    },
  },
  Users: {
    search: async (query, page = 0, size = 10) => {
      const trimmed = query.trim();
      if (!trimmed) return [];

      const response = await userApi.getAllUsers();
      const records = response?.data?.content || response?.data || [];
      const users = Array.isArray(records) ? records : [];
      const normalizedQuery = trimmed.toLowerCase();

      const filtered = users
        .filter((user) => {
          const searchableFields = [
            user?.username,
            user?.name,
            user?.email,
            user?.role,
          ];

          return searchableFields.some((value) =>
            String(value || '').toLowerCase().includes(normalizedQuery)
          );
        });

      const start = page * size;
      const end = start + size;
      const paginatedResults = filtered.slice(start, end);

      return {
        content: paginatedResults,
        totalPages: Math.ceil(filtered.length / size),
        totalElements: filtered.length,
      };
    },
    normalize: (user) => ({
      id: user?.id,
      title: user?.name || user?.username || 'User',
      subtitle: user?.role || '',
      meta: user?.email || '',
      type: 'User',
      details: [
        { label: 'Role', value: user?.role },
        { label: 'ID', value: user?.userCode || user?.id },
      ],
      route: '/dashboard/users',
      original: user,
    }),
    navigate: (result, navigate) => {
      const quickSearch = result?.title || result?.subtitle || result?.meta || result?.original?.name || result?.original?.username || '';
      navigate('/dashboard/users', { state: { selectedUserId: result?.id, quickSearch: quickSearch || undefined } });
    },
  },
};

export const normalizeSearchResult = (type, item) => {
  const config = SEARCH_CONFIG[type];
  if (!config || !item) return null;
  return config.normalize(item);
};

export const searchHeaderResults = async (type, query, page = 0, size = 10) => {
  const config = SEARCH_CONFIG[type];
  if (!config) return { results: [], totalPages: 0, totalElements: 0 };

  try {
    const response = await config.search(query, page, size);
    
    // Handle paginated response format
    if (response && typeof response === 'object' && 'content' in response) {
      const content = Array.isArray(response.content) ? response.content : [];
      const normalized = content
        .map((item) => normalizeSearchResult(type, item))
        .filter(Boolean);
      
      return {
        results: normalized,
        totalPages: response.totalPages || 0,
        totalElements: response.totalElements || 0,
      };
    }
    
    // Handle simple array format (fallback)
    const results = Array.isArray(response) ? response : [];
    const normalized = results
      .map((item) => normalizeSearchResult(type, item))
      .filter(Boolean);
    
    return {
      results: normalized.slice(0, size),
      totalPages: Math.ceil(normalized.length / size),
      totalElements: normalized.length,
    };
  } catch (error) {
    console.error(`Header search failed for ${type}:`, error);
    return { results: [], totalPages: 0, totalElements: 0 };
  }
};

export const getHeaderSearchOptions = () => Object.keys(SEARCH_CONFIG);
